import { FC, useMemo } from 'react';

import { MetricDefinitionNode } from '@/__generated__/graphql';
import { InheritedDescriptionDisplay } from '@/components/Description';
import { NO_DESCRIPTION } from '@/components/DescriptionForm';
import { GeneralResourceTable } from '@/components/ResourcePanel/ResourcePanels/GeneralResourceTable';
import { MetricFilterTable } from '@/components/ResourcePanel/ResourcePanels/MetricFilterTable';
import { SavedQueryParamsTable } from '@/components/ResourcePanel/ResourcePanels/SavedQueryParamsTable';
import { DetailsSection } from '@/components/SectionWithCard';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import {
  GenericResourceNode,
  getAppliedExposure,
  getResourceWithExecutionInfo,
  RESOURCES_WITH_EXECUTION_INFO,
} from '@/hooks/discovery/types';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { truthy } from '@/util/array';
import { ExposureStatusTileSection } from '../Sections/ExposureStatusTileSection';
import { LatestRunStatusSection } from '../Sections/LatestStatusSection';
import { LineageSection } from '../Sections/LineageSection';
import { ReferencesAndDependenciesSection } from '../Sections/ReferencesAndDependencies';
import { SemanticModelEntities } from '../Sections/SemanticModelEntities';
type GeneralViewProps = {
  resource: GenericResourceNode | undefined | null;
  description: string | null | undefined;
  isDescriptionInherited?: boolean | null;
  uniqueId: ParsedUniqueId;
  meta: Record<string, any> | null | undefined;
};

export const GeneralView: FC<GeneralViewProps> = ({
  resource,
  description,
  isDescriptionInherited = false,
  uniqueId,
  meta,
}: GeneralViewProps) => {
  const {
    accountId = 0,
    projectId = 0,
    projectName,
    environmentId,
  } = useDbtCloudProject();

  const { userId } = useEnvironmentScope();

  const metadata = useMemo((): string | null => {
    if (!meta) return null;
    const metadata = JSON.stringify(meta, null, 2);
    /**
     * Ignore {} or []
     */
    if (metadata.length <= 2) return null;
    return metadata;
  }, [meta]);

  const highlightedUniqueIds = useMemo(() => [uniqueId.raw], [uniqueId]);
  const fqn = useMemo(
    () => resource?.fqn?.filter(truthy).join('.') ?? null,
    [resource?.fqn],
  );

  return (
    <div className="opacity-1 duration-1000 motion-reduce:duration-0">
      {resource && (
        <>
          <ExposureStatusTileSection
            appliedExposure={getAppliedExposure(resource)}
            environmentId={environmentId}
            userId={userId === 'loading' ? null : userId}
          />
          <DetailsSection heading="Description">
            <InheritedDescriptionDisplay
              description={description}
              defaultDescription={NO_DESCRIPTION}
              isDescriptionInherited={isDescriptionInherited ?? false}
              inheritedInfo={<span>catalog comment</span>}
              className="prose prose-sm w-full min-w-full dark:prose-invert"
            />
          </DetailsSection>
          {resource.__typename &&
            RESOURCES_WITH_EXECUTION_INFO.includes(resource.__typename) && (
              <LatestRunStatusSection
                resourceType={uniqueId.resourceType}
                applied={getResourceWithExecutionInfo(resource)}
              />
            )}
          {fqn && (
            <LineageSection fqn={fqn} highlightedUniqueIds={highlightedUniqueIds} />
          )}
          <SemanticModelEntities resource={resource} />
          <DetailsSection heading="Metadata" isCompact>
            <div>
              <GeneralResourceTable
                accountId={accountId}
                projectId={projectId}
                projectName={projectName}
                uniqueId={uniqueId}
                isExternalModel={false}
                showAutoExposureActions={false}
              />
            </div>
          </DetailsSection>
          {resource.__typename === 'SavedQueryDefinitionNode' && (
            <SavedQueryParamsTable
              accountId={accountId}
              projectId={projectId}
              projectName={projectName}
              uniqueId={uniqueId}
            />
          )}
          {resource.__typename === 'MetricDefinitionNode' && (
            <MetricFilterTable
              accountId={accountId}
              projectId={projectId}
              projectName={projectName}
              uniqueId={uniqueId}
              metric={resource as unknown as MetricDefinitionNode}
            />
          )}
          {metadata && (
            <DetailsSection heading="Custom Metadata">
              <code className="whitespace-break-spaces">{metadata}</code>
            </DetailsSection>
          )}
          <ReferencesAndDependenciesSection uniqueId={uniqueId} />
        </>
      )}
    </div>
  );
};
